

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { URLUploadData } from "@/lib/types"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import { ragApi } from "@/services/ApiService"

interface URLUploadFormProps {
  categories: {
    id: string
    name: string
    subcategories: {
        id: string
        name: string
    }[]
  }[]
  onUpload: (url: URLUploadData) => Promise<void>
  onCancel: () => void
  loading: boolean
  existingUrls?: string[] // URLs existentes para validar duplicados
}

export function URLUploadForm({ categories, onUpload, onCancel, loading, existingUrls = [] }: URLUploadFormProps) {

  const [title, setTitle] = React.useState("")
  const [url, setUrl] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [selectedCategory, setSelectedCategory] = React.useState("")
  const [selectedSubcategory, setSelectedSubcategory] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)
  const [urlValidationError, setUrlValidationError] = React.useState<string | null>(null)
  const [urlValidationInfo, setUrlValidationInfo] = React.useState<string | null>(null)
  const [isValidatingUrl, setIsValidatingUrl] = React.useState(false)
  const validationRequestIdRef = React.useRef(0)

  // Validar URL en tiempo real
  const validateUrl = React.useCallback(async (urlValue: string) => {
    if (!urlValue.trim()) {
      setUrlValidationError(null)
      setUrlValidationInfo(null)
      return
    }

    // Validar formato básico de URL
    try {
      new URL(urlValue)
    } catch {
      setUrlValidationError('URL inválida')
      setUrlValidationInfo(null)
      return
    }

    // Validar si la URL ya existe
    const normalizedUrl = urlValue.toLowerCase().trim()
    const isDuplicate = existingUrls.some(existingUrl => 
      existingUrl.toLowerCase().trim() === normalizedUrl
    )

    if (isDuplicate) {
      setUrlValidationError('La URL ya existe')
      setUrlValidationInfo(null)
      return
    }

    // URL válida en formato y no duplicada: permitir envío aunque falle verificación remota.
    setUrlValidationError(null)

    // Intentar verificación contra backend para reducir falsos negativos de proxies externos.
    const requestId = ++validationRequestIdRef.current
    try {
      setIsValidatingUrl(true)
      setUrlValidationInfo('Verificando URL...')
      await ragApi.previewURL(urlValue)
      if (validationRequestIdRef.current === requestId) {
        setUrlValidationInfo(null)
      }
    } catch {
      if (validationRequestIdRef.current === requestId) {
        setUrlValidationInfo('No se pudo verificar ahora. Se intentará al añadirla.')
      }
    } finally {
      if (validationRequestIdRef.current === requestId) {
        setIsValidatingUrl(false)
      }
    }
  }, [existingUrls])

  // Debounce para validación de URL
  React.useEffect(() => {
    if (!url.trim()) {
      setUrlValidationError(null)
      setUrlValidationInfo(null)
      return
    }

    const timeoutId = setTimeout(() => {
      validateUrl(url)
    }, 1000) // Esperar 1 segundo después de que el usuario deje de escribir

    return () => clearTimeout(timeoutId)
  }, [url, validateUrl])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title || !url || urlValidationError) return

    // Limpiar error anterior
    setError(null)

    const uploadData: URLUploadData = {
        title,
        url,
        description: description || undefined,
        category: selectedCategory || undefined,
        subcategory: selectedSubcategory || undefined,
        document_type: 'url',
        auto_embed: true,
    }
    
    try {
        await onUpload(uploadData)
    } catch (error) {
        console.error("Upload failed from form component", error)
        // Mostrar error de manera agradable en el formulario
        let errorMessage = 'Error al procesar la URL'
        
        if (error instanceof Error) {
          // Si es un error 400 (Bad Request), probablemente es URL duplicada
          if (error.message.includes('400') || error.message.includes('Bad Request')) {
            errorMessage = 'Esta URL ya existe en la base de conocimiento. Por favor, ingresa una URL diferente.'
          } else {
            errorMessage = error.message
          }
        }
        
        setError(errorMessage)
    }
  }

  const selectedCategoryObj = categories.find((cat) => cat.id === selectedCategory)

  return (
    <div className="relative w-full space-y-4">
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/80 backdrop-blur-sm">
            <div className="text-center">
              <LoadingSpinner size="lg" text="Processing URL..." />
              {url && (
                <p className="mt-2 max-w-xs truncate text-sm text-muted-foreground">
                  Processing: {url}
                </p>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">Título de la URL</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ingresa el título de la URL"
              required
              disabled={loading}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium">URL</Label>
            <Input
              id="url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://ejemplo.com"
              required
              disabled={loading}
              className="h-10"
            />
            {/* Validación simple debajo del input */}
            {urlValidationError && (
              <p className="text-sm mt-1 text-destructive">
                {urlValidationError}
              </p>
            )}
            {!urlValidationError && urlValidationInfo && (
              <p className={`text-sm mt-1 ${urlValidationInfo === 'Verificando URL...' ? 'text-primary' : 'text-warning'}`}>
                {urlValidationInfo}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción breve del contenido de la URL"
              rows={3}
              disabled={loading}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">Categoría</Label>
              <Select
                value={selectedCategory || "__none__"}
                onValueChange={(value) => {
                  if (value === "__none__") {
                    setSelectedCategory("")
                    setSelectedSubcategory("")
                    return
                  }
                  setSelectedCategory(value)
                  setSelectedSubcategory("")
                }}
                disabled={loading}
              >
                <SelectTrigger id="category" className="h-10">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin categoría</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subcategory" className="text-sm font-medium">Subcategoría</Label>
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory} disabled={!selectedCategory || loading}>
                <SelectTrigger id="subcategory" className="h-10">
                  <SelectValue placeholder="Selecciona una subcategoría" />
                </SelectTrigger>
                <SelectContent>
                  {selectedCategoryObj?.subcategories.map((subcat) => (
                    <SelectItem key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

        </form>
        
        {/* Mostrar error de manera agradable */}
        {error && (
          <div className="mt-4 p-4 bg-destructive border border-destructive rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">
                  Error al procesar la URL
                </h3>
                <div className="mt-2 text-sm text-destructive">
                  <p>{error}</p>
                </div>
                <div className="mt-3">
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() => setError(null)}
                    className="text-destructive"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={!title || !url || loading || !!urlValidationError || isValidatingUrl}>
          {loading ? (
            <LoadingSpinner size="sm" text="Añadiendo..." />
          ) : (
            "Añadir URL"
          )}
        </Button>
      </div>
    </div>
  )
}
